
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      const manufacturer = this.getSelectedManufacturer();

      if (manufacturer) {
        try {
          const cartManufacturers = await this.getCartManufacturers();
          const conflictingManufacturers = cartManufacturers.filter(
            (cartManufacturer) => this.normalizeManufacturer(cartManufacturer) !== this.normalizeManufacturer(manufacturer)
          );

          if (conflictingManufacturers.length > 0) {
            const errorMessage = this.getManufacturerConflictMessage(conflictingManufacturers, manufacturer);
            this.handleErrorMessage(errorMessage, true);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: errorMessage,
              message: errorMessage
            });
            this.resetSubmitButton();
            return;
          }

          // A leading underscore keeps this technical line-item property hidden
          // from customers while making future Ajax cart checks reliable.
          formData.set('properties[_rahmenkontor_hersteller]', manufacturer);
        } catch (error) {
          console.error(error);
          const errorMessage = window.manufacturerRestriction?.checkErrorMessage
            || 'Der Warenkorb konnte nicht auf den Hersteller geprüft werden. Bitte versuchen Sie es erneut.';
          this.handleErrorMessage(errorMessage, true);
          this.resetSubmitButton();
          return;
        }
      }

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
    
      //Added to generate selling plan add to cart response
      const sellingPlanId = window.getCurrentSellingPlanId();
      formData.append("selling_plan", sellingPlanId);
      
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    getSelectedManufacturer() {
      if (this.productManufacturer === undefined) {
        const manufacturerData = this.querySelector('[data-manufacturer-restriction-product]');

        try {
          this.productManufacturer = manufacturerData ? JSON.parse(manufacturerData.textContent) : '';
        } catch (error) {
          console.error('Could not read the manufacturer data for this product.', error);
          this.productManufacturer = '';
        }
      }

      return String(this.productManufacturer || '').trim();
    }

    async getCartManufacturers() {
      const response = await fetch(`${routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Cart manufacturer check failed with status ${response.status}.`);

      const cart = await response.json();
      const initialCartItems = window.manufacturerRestriction?.cartItems || [];
      const manufacturers = [];

      cart.items.forEach((item) => {
        const initialCartItem = initialCartItems.find(
          (initialItem) => String(initialItem.variantId) === String(item.variant_id)
        );
        const manufacturer = String(
          item.properties?._rahmenkontor_hersteller
          || initialCartItem?.manufacturer
          || item.vendor
          || ''
        ).trim();

        if (
          manufacturer
          && !manufacturers.some(
            (existingManufacturer) => this.normalizeManufacturer(existingManufacturer) === this.normalizeManufacturer(manufacturer)
          )
        ) {
          manufacturers.push(manufacturer);
        }
      });

      return manufacturers;
    }

    normalizeManufacturer(manufacturer) {
      return String(manufacturer).trim().toLocaleLowerCase('de-DE');
    }

    getManufacturerConflictMessage(cartManufacturers, productManufacturer) {
      const message = window.manufacturerRestriction?.conflictMessage
        || 'Pro Bestellung können nur Rahmen eines Herstellers bestellt werden. Im Warenkorb befinden sich bereits Rahmen von „[cart_manufacturer]“. Entfernen Sie diese zuerst, um Rahmen von „[product_manufacturer]“ hinzuzufügen.';

      return message
        .replace('[cart_manufacturer]', cartManufacturers.join(', '))
        .replace('[product_manufacturer]', productManufacturer);
    }

    resetSubmitButton() {
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-disabled');
      this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
    }

    handleErrorMessage(errorMessage = false, isManufacturerRestriction = false) {
      if (this.hideErrors && !isManufacturerRestriction) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper && isManufacturerRestriction) {
        this.errorMessageWrapper = document.createElement('div');
        this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
        this.errorMessageWrapper.setAttribute('role', 'alert');
        this.errorMessageWrapper.hidden = true;

        const errorMessageElement = document.createElement('span');
        errorMessageElement.className = 'product-form__error-message';
        this.errorMessageWrapper.appendChild(errorMessageElement);
        this.prepend(this.errorMessageWrapper);
      }
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.classList.toggle('manufacturer-restriction-error', Boolean(errorMessage) && isManufacturerRestriction);
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
